import { authenticatedInstance } from '../index/index.service';
import type { OrderlineView } from 'shared-models/browser';
import type { OrderDiscountMode, OrderPrintLogView, OrderView, RestoreTableRequest } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import { asDrawerSelectionError } from '../payments/payments.service';

export type { OrderStatus, OrderType, OrderView, RestoreTableRequest } from 'shared-models/browser';
export type { OrderPrintLogItemView, OrderPrintLogKind, OrderPrintLogStatus, OrderPrintLogView } from 'shared-models/browser';

// Raised when a mutation hits an order the server already closed (404); the client self-heals.
export class OrderAlreadyClosedError extends Error {
  constructor() {
    super('ORDER_ALREADY_CLOSED');
    this.name = 'OrderAlreadyClosedError';
  }
}

// Maps a 404 close failure to the typed already-closed error; null for any other failure.
export const asOrderAlreadyClosedError = (error: unknown): OrderAlreadyClosedError | null => {
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 404 ? new OrderAlreadyClosedError() : null;
};

type OpenOrderPayload = { openByStationary?: string | null };

const normalizeOpenByStationary = (value?: string | null): string | null => {
  const normalized = value?.trim() ?? '';
  if (normalized.length === 0) {
    return null;
  }
  return normalized.slice(0, 50);
};

class OrdersService {
  // Summary: Lists open orders attached to the currently active tenant session so POS Orders panel renders all in-flight tabs in a single te...
  // Details: docs/function-library/web/services.md#orders-service
  async listActiveOpen(tenantId: string, employeeSession: string): Promise<OrderView[]> {
    try {
      const response = await authenticatedInstance.get<OrderView[]>(
        `/orders/tenant/${tenantId}/active`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
        throw new Error(extractServiceErrorMessage(error, 'Failed loading active orders'));
    }
  }

  // Summary: Dashboard open-orders list without x-employee-session dependency.
  async listDashboardOpenOrders(tenantId: string): Promise<OrderView[]> {
    try {
      const response = await authenticatedInstance.get<OrderView[]>(
        `/orders/tenant/${tenantId}/dashboard-open-orders`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading active orders'));
    }
  }

  // Summary: Reads a single tenant order for the order detail screen so direct deep-links remain authorized.
  // Details: docs/function-library/web/services.md#orders-service
  async getById(
    tenantId: string,
    orderId: number,
    employeeSession?: string | null,
  ): Promise<OrderView> {
    try {
      const response = await authenticatedInstance.get<OrderView>(
        `/orders/tenant/${tenantId}/${orderId}`,
        employeeSession
          ? {
              headers: {
                'x-employee-session': employeeSession,
              },
            }
          : undefined,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading order'));
    }
  }

  // Summary: Active-session PAID (closed) orders for the שולם list; OrderView shape.
  // Details: docs/function-library/web/services.md#orders-service
  async listActiveSessionClosed(
    tenantId: string,
    employeeSession: string,
  ): Promise<OrderView[]> {
    try {
      const response = await authenticatedInstance.get<OrderView[]>(
        `/orders/tenant/${tenantId}/active-session-closed-orders`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת ההזמנות ששולמו נכשלה'));
    }
  }

  // Summary: One paid (closed) order by id for the שולם SSE delta; null when it no longer matches
  // the predicate (merge/transfer/void close) so nothing is appended.
  async findActiveSessionClosed(
    tenantId: string,
    orderId: number,
    employeeSession: string,
  ): Promise<OrderView | null> {
    try {
      const response = await authenticatedInstance.get<OrderView | null>(
        `/orders/tenant/${tenantId}/active-session-closed-orders/${orderId}`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data ?? null;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת ההזמנה ששולמה נכשלה'));
    }
  }

  // Summary: Loads the confirmed print-log history for one order.
  async listPrintLogs(
    tenantId: string,
    orderId: number,
    employeeSession?: string | null,
  ): Promise<OrderPrintLogView[]> {
    try {
      const response = await authenticatedInstance.get<OrderPrintLogView[]>(
        `/orders/tenant/${tenantId}/${orderId}/print-logs`,
        employeeSession
          ? {
              headers: {
                'x-employee-session': employeeSession,
              },
            }
          : undefined,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading print logs'));
    }
  }

  // Summary: Loads dashboard order details without x-employee-session for closed-session drilldown.
  async getDashboardOrderDetails(
    tenantId: string,
    orderId: number,
  ): Promise<{ order: OrderView; orderlines: OrderlineView[]; canceledOrderlines: OrderlineView[]; printLogs: OrderPrintLogView[] }> {
    try {
      const response = await authenticatedInstance.get<{ order: OrderView; orderlines: OrderlineView[]; canceledOrderlines: OrderlineView[]; printLogs: OrderPrintLogView[] }>(
        `/orders/tenant/${tenantId}/order-details/${orderId}`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading order details'));
    }
  }

  // Summary: Closed/paid orders for a table in the active session (table-history list).
  // Details: docs/function-library/web/services.md#orders-service
  async listTableHistory(
    tenantId: string,
    tableId: number,
    employeeSession: string,
  ): Promise<OrderView[]> {
    try {
      const response = await authenticatedInstance.get<OrderView[]>(
        `/orders/tenant/${tenantId}/table/${tableId}/history`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת היסטוריית השולחן נכשלה'));
    }
  }

  // Summary: שחזר שולחן (managers only) — clone a closed order onto an available table; 204.
  // Details: docs/function-library/web/services.md#orders-service
  async restoreTable(
    tenantId: string,
    targetTableId: number,
    payload: RestoreTableRequest,
    employeeSession: string,
  ): Promise<void> {
    try {
      const normalizedOpenByStationary = normalizeOpenByStationary(payload.openByStationary);
      await authenticatedInstance.post<void>(
        `/orders/tenant/${tenantId}/table/${targetTableId}/restore`,
        {
          ...payload,
          openByStationary: normalizedOpenByStationary,
        },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'שחזור השולחן נכשל'));
    }
  }

  // Summary: Idempotently opens (or returns existing) open order for a table from POS table-click flow.
  // Details: docs/function-library/web/services.md#orders-service
  async openForTable(
    tenantId: string,
    tableId: number,
    employeeSession: string,
    openByStationary?: string | null,
  ): Promise<void> {
    try {
      const payload: OpenOrderPayload = { openByStationary: normalizeOpenByStationary(openByStationary) };
      await authenticatedInstance.post<void>(
        `/orders/tenant/${tenantId}/tables/${tableId}/open`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
        throw new Error(extractServiceErrorMessage(error, 'Failed opening order'));
    }
  }

  // Summary: Opens a new tableless take-away order from the POS areas-banner take-away button.
  // Details: docs/function-library/web/services.md#orders-service
  async openTakeAway(
    tenantId: string,
    employeeSession: string,
    openByStationary?: string | null,
  ): Promise<void> {
    try {
      const payload: OpenOrderPayload = { openByStationary: normalizeOpenByStationary(openByStationary) };
      await authenticatedInstance.post<void>(
        `/orders/tenant/${tenantId}/take-away/open`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed opening take-away order'));
    }
  }

  // Summary: Opens a new tableless quick-charge order (QC_ code) via the shared take-away path.
  // Details: docs/function-library/web/services.md#orders-service
  async openQuickCharge(
    tenantId: string,
    employeeSession: string,
    openByStationary?: string | null,
  ): Promise<void> {
    try {
      const payload: OpenOrderPayload = { openByStationary: normalizeOpenByStationary(openByStationary) };
      await authenticatedInstance.post<void>(
        `/orders/tenant/${tenantId}/take-away/quick-charge`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed opening quick-charge order'));
    }
  }

  async openKioskDineIn(
    tenantId: string,
    employeeSession: string,
    openByStationary?: string | null,
  ): Promise<void> {
    try {
      const payload: OpenOrderPayload = { openByStationary: normalizeOpenByStationary(openByStationary) };
      await authenticatedInstance.post<void>(
        `/orders/tenant/${tenantId}/take-away/kiosk-dine-in`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed opening kiosk dine-in order'));
    }
  }

  // Summary: On exit, close a take-away order if it never had an orderline (state א).
  // Details: docs/function-library/web/services.md#orders-service
  async closeTakeAwayOnExit(
    tenantId: string,
    orderId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/take-away/exit`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch {
      // Best-effort cleanup on exit; the 30-min sweep is the guaranteed fallback.
    }
  }

  // Summary: Closes an open tenant order via order detail "Close Order" toggle.
  // Details: docs/function-library/web/services.md#orders-service
  async close(
    tenantId: string,
    orderId: number,
    employeeSession: string,
    cashDrawerPrinterId?: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/close`,
        cashDrawerPrinterId != null ? { cashDrawerPrinterId } : {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      // Multi-drawer settled close needs a drawer: surface the typed error for the picker.
      const drawerError = asDrawerSelectionError(error);
      if (drawerError) {
        throw drawerError;
      }
      // A 404 means the order was already closed elsewhere: surface the typed self-heal signal.
      const closedError = asOrderAlreadyClosedError(error);
      if (closedError) {
        throw closedError;
      }
      throw new Error(extractServiceErrorMessage(error, 'Failed closing order'));
    }
  }

  // Summary: Updates guest name on an order for the receipt screen guest-name field. Returns void — updated OrderView arrives via order.upserted SSE.
  async updateGuestName(
    tenantId: string,
    orderId: number,
    guestName: string | null,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/guest-name`,
        { guestName },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating guest name'));
    }
  }

  // Summary: Manager reassigns order opener; updated order arrives via order.upserted SSE.
  async updateOpeningWaiter(
    tenantId: string,
    orderId: number,
    openedByEmployeeId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/opening-waiter`,
        { openedByEmployeeId },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating opening waiter'));
    }
  }

  // Summary: Toggle per-order deposit flag; void (update via order.upserted SSE).
  async updateOrderDeposit(
    tenantId: string,
    orderId: number,
    hasDeposit: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/deposit`,
        { hasDeposit },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating deposit'));
    }
  }

  // Summary: Applies per-item discounts (split + write). Returns void — updated order arrives via order.upserted SSE; orderlines update via orderline SSE events.
  async applyItemDiscount(
    tenantId: string,
    orderId: number,
    items: { orderlineId: number; quantity: number; discountInput: number | null }[],
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/item-discount`,
        { items },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed applying item discount'));
    }
  }

  // Summary: Cancels selected finalized receipt orderline quantities. Returns void — order/orderline convergence arrives via SSE.
  // Details: docs/function-library/web/services.md#orders-service
  async cancelOrderlines(
    tenantId: string,
    orderId: number,
    items: { orderlineId: number; quantity: number }[],
    employeeSession: string,
    fullTableCancel = false,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/cancel-orderlines`,
        { items, ...(fullTableCancel ? { fullTableCancel: true } : {}) },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed canceling orderlines'));
    }
  }

  // Summary: Updates diners count on an open order. Returns void — updated OrderView arrives via order.upserted SSE.
  async updateDinersCount(
    tenantId: string,
    orderId: number,
    dinersCount: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/diners`,
        { dinersCount },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating diners count'));
    }
  }

  // Summary: Sets an order's VIP flag; updated OrderView arrives via order.upserted SSE.
  async updateOrderVip(
    tenantId: string,
    orderId: number,
    isVip: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/vip`,
        { isVip },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating VIP status'));
    }
  }

  // Summary: Sets or clears global discount on an open order. Returns void — updated OrderView (with discountMode/discountInput) arrives via order.upserted SSE.
  async updateGlobalDiscount(
    tenantId: string,
    orderId: number,
    discountMode: OrderDiscountMode | null,
    discountInput: number | null,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orders/tenant/${tenantId}/${orderId}/discount`,
        { discountMode, discountInput },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed applying discount'));
    }
  }
}

export const ordersService = new OrdersService();


